import type { BaseVcsCallConfig } from 'api/generated/models/BaseVcsCallConfig';
import type { JoinCallByLinkResponseV2 } from 'api/generated/models/JoinCallByLinkResponseV2';
import type { JoinCallResponseV2 } from 'api/generated/models/JoinCallResponseV2';
import type { RoomModeratorRaw, WebinarModeData } from 'api/types';
import { mapWebinarModeFromJoin } from 'useCase/webinar/mapWebinarModeFromJoin';
import { mapModeratorsFromJoin } from 'useCase/moderation/utils/mapModeratorsFromJoin';
import { isJoinRejectedError } from './joinErrors';

export type JoinCredentials = { jwtToken: string; wssUrl: string };

const fromVideoSettings = (settings: { jwtToken?: string; url?: string } | undefined): JoinCredentials | null => {
    const jwtToken = settings?.jwtToken;
    const wssUrl = settings?.url;
    if (!jwtToken || !wssUrl) {
        return null;
    }
    return { jwtToken, wssUrl };
};

export const getWaitingListIdFromJoinByLink = (payload?: JoinCallByLinkResponseV2): string | undefined =>
    payload?.waitingInfo?.waitingListId;

export const getWaitingListIdFromJoinCall = (payload?: JoinCallResponseV2): string | undefined =>
    payload?.waitingInfo?.waitingListId;

export const getWebinarModeFromJoinByLink = (payload?: JoinCallByLinkResponseV2): WebinarModeData | undefined =>
    mapWebinarModeFromJoin(payload?.webinarMode);

export const getWebinarModeFromJoinCall = (payload?: JoinCallResponseV2): WebinarModeData | undefined =>
    mapWebinarModeFromJoin(payload?.webinarMode);

export const getModeratorsFromJoinByLink = (payload?: JoinCallByLinkResponseV2): RoomModeratorRaw[] | undefined =>
    mapModeratorsFromJoin(payload?.moderators);

export const getModeratorsFromJoinCall = (payload?: JoinCallResponseV2): RoomModeratorRaw[] | undefined =>
    mapModeratorsFromJoin(payload?.moderators);

export const extractJoinCredentialsFromJoinByLink = (
    payload: JoinCallByLinkResponseV2 | undefined,
): JoinCredentials | null => fromVideoSettings(payload?.settings);

export const extractJoinCredentialsFromJoinCall = (payload: JoinCallResponseV2 | undefined): JoinCredentials | null => {
    const config = payload?.config as BaseVcsCallConfig | undefined;
    return fromVideoSettings(config?.vcsProviderConfigs?.videoConferenceSettings);
};

export type JoinPollResult =
    | { kind: 'admitted'; credentials: JoinCredentials; webinarMode?: WebinarModeData; moderators?: RoomModeratorRaw[] }
    | { kind: 'waiting'; waitingListId: string; webinarMode?: WebinarModeData; moderators?: RoomModeratorRaw[] }
    | { kind: 'error'; error: unknown };

const resolvePollError = (error: unknown, requestWaitingListId?: string): JoinPollResult => {
    if (requestWaitingListId && isJoinRejectedError(error)) {
        return { kind: 'error', error: 'JOIN_REJECTED' };
    }
    return { kind: 'error', error };
};

const resolveJoinPoll = (
    error: unknown | undefined,
    extractCredentials: () => JoinCredentials | null,
    extractWaitingId: () => string | undefined,
    extractWebinarMode: () => WebinarModeData | undefined,
    extractModerators: () => RoomModeratorRaw[] | undefined,
    requestWaitingListId?: string,
): JoinPollResult => {
    if (error) return resolvePollError(error, requestWaitingListId);
    const webinarMode = extractWebinarMode();
    const moderators = extractModerators();
    const credentials = extractCredentials();
    if (credentials) return { kind: 'admitted', credentials, webinarMode, moderators };
    const responseWaitingListId = extractWaitingId();

    // При poll передаём requestWaitingListId - не подменяем его новым id из ответа (иначе баннер у модератора мигает).
    const waitingListId = requestWaitingListId ?? responseWaitingListId;
    if (waitingListId) return { kind: 'waiting', waitingListId, webinarMode, moderators };
    return { kind: 'error', error: new Error('Не удалось получить данные для подключения к комнате') };
};

export const resolveJoinByLinkPoll = (
    payload: JoinCallByLinkResponseV2 | undefined,
    error: unknown | undefined,
    requestWaitingListId?: string,
): JoinPollResult =>
    resolveJoinPoll(
        error,
        () => extractJoinCredentialsFromJoinByLink(payload),
        () => getWaitingListIdFromJoinByLink(payload),
        () => getWebinarModeFromJoinByLink(payload),
        () => getModeratorsFromJoinByLink(payload),
        requestWaitingListId,
    );

export const resolveJoinCallPoll = (
    payload: JoinCallResponseV2 | undefined,
    error: unknown | undefined,
    requestWaitingListId?: string,
): JoinPollResult =>
    resolveJoinPoll(
        error,
        () => extractJoinCredentialsFromJoinCall(payload),
        () => getWaitingListIdFromJoinCall(payload),
        () => getWebinarModeFromJoinCall(payload),
        () => getModeratorsFromJoinCall(payload),
        requestWaitingListId,
    );
